import React, { useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import AuthCallback from './components/auth/AuthCallback';
import PasswordReset from './components/auth/PasswordReset';
import { CoolClimateNavigation } from './components/layout/CoolClimateNavigation';
import { Footer } from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { Navigation } from './components/layout/Navigation';
import { CalculatorPage } from './components/pages/CalculatorPage';
import { CoolClimateCalculator } from './components/pages/CoolClimateCalculator';
import { Energy } from './components/pages/Energy';
import { GHGInventory } from './components/pages/GHGInventory';
import { Other } from './components/pages/Other';
import { Overview } from './components/pages/Overview';
import { People } from './components/pages/People';
import { Community } from './components/pages/Community';
import SimpleDevelopersLogPage from './components/pages/SimpleDevelopersLogPage';
import { Transportation } from './components/pages/Transportation';
import UserManagementPage from './components/pages/UserManagementPage';
import ChangeLog from './components/pages/ChangeLog';
import DeveloperPage from './components/pages/DeveloperPage';
import { PolicyTool } from './components/pages/PolicyTool';
import AdminSettingsPage from './components/pages/AdminSettingsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChartPngRegistryProvider } from './contexts/ChartPngRegistryContext';
import { LocationProvider, useLocation as useLocationHook } from './contexts/LocationContext';
import { UserRoleProvider } from './contexts/UserRoleContext';
import { supabaseLocationService } from './services/supabaseLocationService';


function PolicyToolRoute() {
  // Policy Tool is now available to all users
  return <PolicyTool />;
}

function LegacyRedirect({ section }: { section: string }) {
  const { selectedLocation } = useLocationHook();
  if (!selectedLocation) {
    return <Navigate to="/" replace />;
  }
  const slug = supabaseLocationService.getSlugForLocation(selectedLocation);
  if (!slug) {
    return <Navigate to="/" replace />;
  }
  const target = section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
  return <Navigate to={target} replace />;
}

/** Legacy v1 app at /v1: full-page iframe. Dev: localhost:3001/v1; prod: same-origin /v1. */
function V1LegacyFrame() {
  const location = useLocation();
  const v1Path = location.pathname; // /v1 or /v1/overview etc.
  const v1Src =
    process.env.NODE_ENV === 'development'
      ? `http://localhost:3001${v1Path}`
      : `${window.location.origin}${v1Path}`;
  return (
    <iframe
      src={v1Src}
      title="ClimatePlans v1"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: 'none',
        display: 'block',
      }}
    />
  );
}

// Build marker so we can confirm production is serving the latest deploy (check browser console)
const APP_BUILD_ID = 'climateplans-2025-01-26-v1-deploy';
if (typeof console !== 'undefined' && console.log) {
  console.log(`[ClimatePlans] App bundle: ${APP_BUILD_ID}`);
}

function AppContent() {
  const { selectedLocation, selectLocation, locationData } = useLocationHook();
  const { loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isLandingPage = location.pathname === '/';
  
  // Check if we're on the CoolClimate Calculator page
  const isCoolClimatePage = location.pathname === '/coolclimate';

  // Legacy v1 app: hide main app header and nav so only the iframe shows
  const isV1Page = location.pathname.startsWith('/v1');
  if (isV1Page && typeof console !== 'undefined' && console.log) {
    console.log(`[ClimatePlans] Path /v1 detected, rendering legacy v1 frame. pathname=${location.pathname}`);
  }

  const getSectionFromPath = (pathname: string) => {
    const parts = pathname.split('/').filter(Boolean);
    if (!parts.length) return 'overview';

    const knownSections = new Set([
      'overview',
      'people',
      'ghg',
      'buildings',
      'transportation',
      'policy-tool',
      'community',
      'other',
      'admin-settings',
      'coolclimate-calculator'
    ]);

    // Legacy path style: /ghg, /people, etc.
    if (knownSections.has(parts[0])) {
      return parts[0];
    }

    // Slugged path style: /oakland/ghg, /oakland/people, etc.
    if (parts.length >= 2 && knownSections.has(parts[1])) {
      return parts[1];
    }

    // Slug-only path defaults to overview.
    return 'overview';
  };

  const buildLocationPath = (slug: string | null, section: string) => {
    if (!slug) {
      return section === 'overview' ? '/overview' : `/${section}`;
    }
    return section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
  };

  const handleLocationSelect = (selectedLocationName: string) => {
    selectLocation(selectedLocationName);
    const slug = supabaseLocationService.getSlugForLocation(selectedLocationName);
    const section = getSectionFromPath(location.pathname);
    const nextPath = buildLocationPath(slug, section);
    const nextUrl = `${nextPath}${location.search || ''}${location.hash || ''}`;
    const currentUrl = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    if (currentUrl !== nextUrl) {
      navigate(nextUrl);
    }
  };

  const handleNavigateToOverview = () => {
    const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
    navigate(slug ? `/${slug}` : '/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionChange = (section: string) => {
    // Check if we're on Overview page and if it's fully loaded
    const isOnOverviewPage = location.pathname === '/' || location.pathname === '/overview';
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;
    
    // If on Overview page and not fully loaded, block navigation to pages other than Transportation
    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
    const nextPath = buildLocationPath(slug, section);
    navigate(nextPath);
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!locationData?.availableLocations?.length) return;
    const parts = location.pathname.split('/').filter(Boolean);
    if (!parts.length) return;
    const slug = parts[0];
    // Never treat reserved path segments as a location slug
    if (slug === 'v1' || slug === 'coolclimate-calculator') return;
    const resolved = supabaseLocationService.getLocationBySlug(slug);
    if (resolved && resolved.name !== selectedLocation) {
      selectLocation(resolved.name);
    }
  }, [location.pathname, locationData, selectedLocation, selectLocation]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Allow unauthenticated access - no login requirement

  // Render v1 legacy app before any Routes so /v1 is never matched as /:locationSlug
  if (isV1Page) {
    return (
      <div className="App">
        <main className="main-content main-content--v1-fullscreen">
          <V1LegacyFrame />
        </main>
      </div>
    );
  }

  const locationSlug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;

  return (
    <div className="App">
      {/* Skip link for keyboard and screen reader users (ADA) */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {!isV1Page && (
        <>
          <Header 
            onNavigateToOverview={handleNavigateToOverview}
            onLocationSelect={handleLocationSelect}
            currentLocation={selectedLocation}
          />
          {/* Show appropriate navigation based on page */}
          {!isLandingPage && !isCoolClimatePage && (
            <Navigation
              onSectionChange={handleSectionChange}
              locationSlug={locationSlug}
              currentPath={location.pathname}
            />
          )}
          {isCoolClimatePage && <CoolClimateNavigation />}
        </>
      )}
      <main id="main-content" className={isV1Page ? 'main-content main-content--v1-fullscreen' : 'main-content'} tabIndex={-1}>
        <Routes>
          <Route 
            path="/" 
            element={
              <Overview onLocationSelect={handleLocationSelect} isLandingPage={true} />
            } 
          />
          {/* Legacy v1 app: full-page iframe so / = main app, /v1 = old app; back goes to / */}
          <Route path="/v1/*" element={<V1LegacyFrame />} />
          <Route
            path="/overview"
            element={<LegacyRedirect section="overview" />}
          />
          <Route
            path="/:locationSlug"
            element={
              <Overview onLocationSelect={handleLocationSelect} isLandingPage={false} />
            }
          />
          <Route
            path="/:locationSlug/overview"
            element={
              <Overview onLocationSelect={handleLocationSelect} isLandingPage={false} />
            }
          />
          <Route 
            path="/people" 
            element={<LegacyRedirect section="people" />} 
          />
          <Route
            path="/:locationSlug/people"
            element={
              <People location={selectedLocation} />
            }
          />
          <Route 
            path="/ghg" 
            element={<LegacyRedirect section="ghg" />} 
          />
          <Route
            path="/:locationSlug/ghg"
            element={
              <GHGInventory location={selectedLocation} />
            }
          />
          <Route 
            path="/buildings" 
            element={<LegacyRedirect section="buildings" />} 
          />
          <Route
            path="/:locationSlug/buildings"
            element={
              <Energy location={selectedLocation} />
            }
          />
          <Route 
            path="/transportation" 
            element={<LegacyRedirect section="transportation" />} 
          />
          <Route
            path="/:locationSlug/transportation"
            element={
              <Transportation location={selectedLocation} />
            }
          />
          {/* Policy Tool route - available to all users */}
          <Route
            path="/policy-tool"
            element={<LegacyRedirect section="policy-tool" />}
          />
          <Route
            path="/:locationSlug/policy-tool"
            element={<PolicyToolRoute />}
          />
          <Route
            path="/community"
            element={<LegacyRedirect section="community" />}
          />
          <Route
            path="/:locationSlug/community"
            element={
              <Community />
            }
          />
          <Route
            path="/changelog"
            element={
              <ChangeLog />
            }
          />
          <Route
            path="/other"
            element={<LegacyRedirect section="other" />}
          />
          <Route
            path="/:locationSlug/other"
            element={
              <Other />
            }
          />
          <Route
            path="/admin-settings"
            element={<LegacyRedirect section="admin-settings" />}
          />
          <Route
            path="/:locationSlug/admin-settings"
            element={<AdminSettingsPage />}
          />
          {/* Calculator (dev/admin only): /coolclimate-calculator and /:locationSlug/coolclimate-calculator */}
          <Route
            path="/coolclimate-calculator"
            element={<LegacyRedirect section="coolclimate-calculator" />}
          />
          <Route
            path="/:locationSlug/coolclimate-calculator"
            element={<CalculatorPage />}
          />
          <Route 
            path="/coolclimate" 
            element={
              <CoolClimateCalculator />
            } 
          />
                      <Route 
                        path="/developers-log" 
                        element={
                          <SimpleDevelopersLogPage />
                        } 
                      />
          <Route 
            path="/user-management" 
            element={
              <UserManagementPage />
            } 
          />
          <Route 
            path="/developer/:developerSlug" 
            element={
              <DeveloperPage />
            } 
          />
          <Route 
            path="/auth/callback" 
            element={
              <AuthCallback />
            } 
          />
          <Route 
            path="/auth/reset-password" 
            element={
              <PasswordReset />
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      
      {/* Footer - Appears on all pages */}
      <Footer />
    </div>
  );
}

function AppWithUserRole() {
  const { user } = useAuth();
  
  // Always render AppContent, with or without user
  // UserRoleProvider will handle the case where userId is null/undefined
  return (
    <UserRoleProvider userId={user?.id} user={user ?? undefined}>
      <AppContent />
    </UserRoleProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <ChartPngRegistryProvider>
          <Router>
            <AppWithUserRole />
          </Router>
        </ChartPngRegistryProvider>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;